import React from 'react';
import { appInsightsService } from 'src/services';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import PropTypes from 'prop-types';
import { SomethingWentWrongView } from 'src/views';
import { Box, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { formatDateISOWithSeconds } from 'src/utils/date'

const styles = theme => ({
  developerDetailsSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    maxWidth: '400px',
    textTransform: 'uppercase',
  },
  stackTrace: {
    background: '#f5f5f5',
    padding: theme.spacing(2),
    overflow: 'auto',
    fontSize: theme.typography.body2.fontSize,
    borderRadius: theme.shape.borderRadius,
    border: '1px solid #ddd',
    maxHeight: '300px',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
  },
  summary:{
    cursor: 'pointer', 
    fontWeight: 'bold',
    padding: '8px',
    userSelect: 'none',
  }
});

/**
 * Generic error boundary component to catch JavaScript errors and display a fallback UI
 * Also logs errors to Application Insights with timestamp and invocation ID for debugging
 * @see https://reactjs.org/docs/error-boundaries.html
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            timestamp: null,
        };
    }

    static getDerivedStateFromError(error) {
        return {
            hasError: true,
            timestamp: new Date().toISOString(),
        };
    }

    componentDidCatch(error, errorInfo) {
        const { timestamp } = this.state;
        let appInsightsOperationId = null;
        if (appInsightsService?.appInsights) {
            try {
                appInsightsService.appInsights.trackException({
                    exception: error,
                    severityLevel: SeverityLevel.Error,
                    properties: {
                        componentStack: errorInfo.componentStack,
                        timestamp,
                        errorBoundary: this.props.boundaryName || 'ErrorBoundary',
                        ...(this.props.customProperties || {}),
                    },
                });
                const telemetryContext = appInsightsService.appInsights.context;
                appInsightsOperationId = telemetryContext?.telemetryTrace?.traceID || 
                                        telemetryContext?.telemetryTrace?.name ||
                                        'N/A';
            } catch (loggingError) {
                console.error('Failed to log error to Application Insights:', loggingError);
            }
        }
        const invocationId = appInsightsOperationId;
        this.setState({
            error,
            errorInfo,
            invocationId,
        });
        if (this.props.onError) {
            this.props.onError(error, errorInfo, invocationId);
        }
        if (process.env.ENV !== 'prod') {
            console.error('ErrorBoundary caught an error:', error, errorInfo);
        }
    }

    render() {
        const { hasError, error, errorInfo, timestamp, invocationId } = this.state;
        const { children, fallback, classes } = this.props;
        const errorMessage = error ? error.toString() : 'An unexpected error occurred.';
        if (hasError) {
            if (fallback) {
                return typeof fallback === 'function'
                    ? fallback({ error, errorInfo, timestamp, invocationId })
                    : fallback;
            }
            
            const messageContent = (
                <Box mt={3} display="flex" flexDirection="column" alignItems="center">
                    <Box className={classes.developerDetailsSection}>
                        <Typography variant="body2" color="textSecondary" gutterBottom align="left">
                            Reference Id: <strong>{invocationId || 'N/A'}</strong>
                        </Typography>
                        <Typography variant="body2" color="textSecondary" display="block" align="left">
                            Timestamp: <strong>{formatDateISOWithSeconds(timestamp)}</strong>
                        </Typography>
                    </Box>
                    {errorInfo && (
                        <Box mt={2}>
                            <details style={{ textAlign: 'left' }}>
                                <summary className ={classes.summary} aria-label="Developer Information" role="button" tabIndex={0}>
                                    🔧 Developer Info (Click to expand)
                                </summary>
                                <Box mt={1}>
                                    <Typography variant="body2" color="error" gutterBottom>
                                        <strong>Error:</strong> {errorMessage}
                                    </Typography>
                                    <pre className={classes.stackTrace}>
                                        {errorInfo.componentStack}
                                    </pre>
                                </Box>
                            </details>
                        </Box>
                    )}
                </Box>
            );
            return (
                <SomethingWentWrongView
                    message={messageContent}
                    renderLogoutButton={false}
                    renderImageFirst={true}
                />
            );
        }
        return children;
    }
}

ErrorBoundary.propTypes = {
    children: PropTypes.node.isRequired,
    /** Custom fallback UI (component or render function) */
    fallback: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    /** Optional callback when error is caught */
    onError: PropTypes.func,
    /** Name to identify this boundary in telemetry */
    boundaryName: PropTypes.string,
    /** Additional custom properties to log to Application Insights */
    customProperties: PropTypes.object,
};

export default  withStyles(styles)(ErrorBoundary);
